import React, { useState, useEffect } from "react";
import Card from "../../components/Card";
import Action from "../../components/Action";
import RadioConditional from "../../components/RadioConditional";
import RadioQuestion from "../../components/RadioQuestion";
import TextQuestion from "../../components/TextQuestion";
import ScrollToTop from "../../components/utils/ScrollToTop";
import { YES_NO_OPTIONS } from "../../constants";
import Image from "../../components/Image";
import imageCatAndHousePlant from "../../images/cat-and-houseplant.png";
import { isMoneyValid, normaliseMoney } from "../../shared/utils/moneyUtils";
import { getOrdinalString } from "../../shared/utils/numberUtils";
import { isEmailValid } from "../../shared/utils/validation";

const defaultErrorMessages = {
  giftingMoneyFullName: "Please fill this in",
  giftingMoneyAmount: "Please fill this in",
  giftingMoneyEmailAddress: "Please enter an email address",
  giftingMoneyRelationship: "Please fill this in",
  giftingMoneyJointGift: "Please choose an option",
  giftingMoneyJointName: "Please fill this in",
  giftingMoneyJointEmailAddress: "Please enter an email address",
  giftingMoneyJointRelationship: "Please fill this in",
};

const initStateFromProps = (giftingMoney) => {
  if (giftingMoney) {
    return {
      giftingMoneyFullName: giftingMoney.giftingMoneyFullName || "",
      giftingMoneyAmount: giftingMoney.giftingMoneyAmount || "",
      giftingMoneyEmailAddress: giftingMoney.giftingMoneyEmailAddress || "",
      giftingMoneyRelationship: giftingMoney.giftingMoneyRelationship || "",
      giftingMoneyJointGift: giftingMoney.giftingMoneyJointGift ?? null,
      giftingMoneyJointName: giftingMoney.giftingMoneyJointName || "",
      giftingMoneyJointEmailAddress: giftingMoney.giftingMoneyJointEmailAddress || "",
      giftingMoneyJointRelationship: giftingMoney.giftingMoneyJointRelationship || "",
    };
  }
  return {
    giftingMoneyFullName: "",
    giftingMoneyAmount: "",
    giftingMoneyEmailAddress: "",
    giftingMoneyRelationship: "",
    giftingMoneyJointGift: null,
    giftingMoneyJointName: "",
    giftingMoneyJointEmailAddress: "",
    giftingMoneyJointRelationship: "",
  };
};

const GiftingMoneyForm = ({ giftingMoney, giftNumber, onStepComplete }) => {
  const [formState, setFormState] = useState(initStateFromProps(giftingMoney));
  const [errors, setErrors] = useState({});
  const [errorMessages, setErrorMessages] = useState({ ...defaultErrorMessages });

  // Reset form state when props change (e.g., navigating between gifts)
  useEffect(() => {
    setFormState(initStateFromProps(giftingMoney));
    setErrors({});
    setErrorMessages({ ...defaultErrorMessages });
  }, [giftingMoney, giftNumber]);

  const handleInputChange = (field) => (value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const isJointEmailSameAsFirstEmail = (jointEmail, firstEmail) => {
    return jointEmail && firstEmail && jointEmail.toLowerCase() === firstEmail.toLowerCase();
  };

  const handleValidation = () => {
    let newErrors = {};
    let newErrorMessages = { ...errorMessages };
    let formIsValid = true;

    if (!formState.giftingMoneyFullName) {
      newErrors.giftingMoneyFullName = true;
      formIsValid = false;
    }

    if (!formState.giftingMoneyAmount) {
      newErrors.giftingMoneyAmount = true;
      formIsValid = false;
    }

    if (formState.giftingMoneyAmount !== "" && !isMoneyValid(formState.giftingMoneyAmount)) {
      newErrors.giftingMoneyAmount = true;
      newErrorMessages.giftingMoneyAmount = "Please enter a valid amount";
      formIsValid = false;
    }

    if (!formState.giftingMoneyEmailAddress) {
      newErrors.giftingMoneyEmailAddress = true;
      newErrorMessages.giftingMoneyEmailAddress = "Please enter an email address";
      formIsValid = false;
    }

    if (formState.giftingMoneyEmailAddress !== "" && !isEmailValid(formState.giftingMoneyEmailAddress)) {
      newErrors.giftingMoneyEmailAddress = true;
      newErrorMessages.giftingMoneyEmailAddress = "Please enter a valid email address";
      formIsValid = false;
    }

    if (!formState.giftingMoneyRelationship) {
      newErrors.giftingMoneyRelationship = true;
      formIsValid = false;
    }

    if (formState.giftingMoneyJointGift === null) {
      newErrors.giftingMoneyJointGift = true;
      formIsValid = false;
    }

    if (formState.giftingMoneyJointGift === true) {
      if (!formState.giftingMoneyJointName) {
        newErrors.giftingMoneyJointName = true;
        formIsValid = false;
      }
      if (!formState.giftingMoneyJointEmailAddress) {
        newErrors.giftingMoneyJointEmailAddress = true;
        newErrorMessages.giftingMoneyJointEmailAddress = "Please enter an email address";
        formIsValid = false;
      }
      if (formState.giftingMoneyJointEmailAddress !== "" && !isEmailValid(formState.giftingMoneyJointEmailAddress)) {
        newErrors.giftingMoneyJointEmailAddress = true;
        newErrorMessages.giftingMoneyJointEmailAddress = "Please enter a valid email address";
        formIsValid = false;
      }
      // Check joint email address and first gifter's email address differ
      if (isJointEmailSameAsFirstEmail(formState.giftingMoneyJointEmailAddress, formState.giftingMoneyEmailAddress)) {
        newErrors.giftingMoneyJointEmailAddress = true;
        newErrorMessages.giftingMoneyJointEmailAddress = `This can’t be the same email address as ${
          formState.giftingMoneyEmailAddress
        }`;
        formIsValid = false;
      }
      if (!formState.giftingMoneyJointRelationship) {
        newErrors.giftingMoneyJointRelationship = true;
        formIsValid = false;
      }
    }

    setErrors(newErrors);
    setErrorMessages(newErrorMessages);
    return formIsValid;
  };

  const handleFieldValidation = (field) => (event) => {
    const isBlur = event.type === "blur";
    setErrors((prev) => {
      let newErrors = { ...prev };

      if (!isBlur) {
        newErrors[field] = false;
      } else {
        if (!formState[field]) {
          newErrors[field] = true;
        }
      }

      return newErrors;
    });
  };

  const handleRadioValidation = (field) => (event) => {
    const isBlur = event.type === "blur";
    setErrors((prev) => {
      let newErrors = { ...prev };

      if (!isBlur) {
        newErrors[field] = false;
      } else {
        if (formState[field] === null) {
          newErrors[field] = true;
        }
      }

      return newErrors;
    });
  };

  const handleEmailAddressValidation = (field) => (event) => {
    const isBlur = event.type === "blur";
    setErrors((prev) => {
      let newErrors = { ...prev };

      if (!isBlur) {
        newErrors[field] = false;
      } else {
        if (!formState[field]) {
          newErrors[field] = true;
        } else if (!isEmailValid(formState[field])) {
          newErrors[field] = true;
        }
        // Check if joint email address matches the first gifter's email address
        if (
          field === "giftingMoneyJointEmailAddress" &&
          isJointEmailSameAsFirstEmail(formState[field], formState.giftingMoneyEmailAddress)
        ) {
          newErrors[field] = true;
        }
      }

      return newErrors;
    });
    setErrorMessages((prev) => {
      let newErrorMessages = { ...prev };

      if (isBlur) {
        if (!formState[field]) {
          newErrorMessages[field] = "Please enter an email address";
        } else if (!isEmailValid(formState[field])) {
          newErrorMessages[field] = "Please enter a valid email address";
        }
        // Check if joint email address matches the first gifter's email address
        if (
          field === "giftingMoneyJointEmailAddress" &&
          isJointEmailSameAsFirstEmail(formState[field], formState.giftingMoneyEmailAddress)
        ) {
          newErrorMessages[field] = `This can’t be the same email address as ${formState.giftingMoneyEmailAddress}`;
        }
      }

      return newErrorMessages;
    });
  };

  const handleMoneyValidation = (field) => (event) => {
    const isBlur = event.type === "blur";
    setErrors((prev) => {
      let newErrors = { ...prev };

      if (!isBlur) {
        newErrors[field] = false;
      } else {
        if (!formState[field]) {
          newErrors[field] = true;
        } else if (!isMoneyValid(formState[field])) {
          newErrors[field] = true;
        }
      }

      return newErrors;
    });
    setErrorMessages((prev) => {
      let newErrorMessages = { ...prev };

      if (isBlur) {
        if (!formState[field]) {
          newErrorMessages[field] = "Please fill this in";
        } else if (!isMoneyValid(formState[field])) {
          newErrorMessages[field] = "Please enter a valid amount";
        }
      }

      return newErrorMessages;
    });
  };

  const saveGiftingMoney = (event) => {
    event.preventDefault();

    if (handleValidation()) {
      const outGift = {};
      outGift["giftingMoney" + giftNumber] = {
        giftingMoneyFullName: formState.giftingMoneyFullName,
        giftingMoneyAmount: normaliseMoney(formState.giftingMoneyAmount),
        giftingMoneyEmailAddress: formState.giftingMoneyEmailAddress,
        giftingMoneyRelationship: formState.giftingMoneyRelationship,
        giftingMoneyJointGift: formState.giftingMoneyJointGift,
        giftingMoneyJointName: formState.giftingMoneyJointName,
        giftingMoneyJointEmailAddress: formState.giftingMoneyJointEmailAddress,
        giftingMoneyJointRelationship: formState.giftingMoneyJointRelationship,
      };

      onStepComplete({
        ...outGift,
      });
    }
  };

  const ordinalString = getOrdinalString(giftNumber);

  return (
    <React.Fragment>
      <ScrollToTop />
      <Card title={"Tell us about the " + ordinalString + " gift of money"} removeTopPadding>
        <p>
          We need to know how much money you are receiving from someone who is still alive - as a gift towards your
          purchase.
        </p>
        <p>
          Tell us the name and email address of anyone giving you money - so we can contact them to verify their
          identify and complete our anti-money laundering checks.
        </p>
        <TextQuestion
          question={"What is the name of the " + ordinalString + " person giving you money?"}
          value={formState.giftingMoneyFullName}
          valueEntered={handleInputChange("giftingMoneyFullName")}
          validate={handleFieldValidation("giftingMoneyFullName")}
          validationError={errors.giftingMoneyFullName ? errorMessages.giftingMoneyFullName : null}
        />

        <TextQuestion
          question="How much will they be giving you?"
          value={formState.giftingMoneyAmount}
          valueEntered={handleInputChange("giftingMoneyAmount")}
          validate={handleMoneyValidation("giftingMoneyAmount")}
          validationError={errors.giftingMoneyAmount ? errorMessages.giftingMoneyAmount : null}
        />

        <TextQuestion
          question="What’s their email address?"
          hint="This must belong to the gifter only — not anyone else involved in the case"
          value={formState.giftingMoneyEmailAddress}
          valueEntered={handleInputChange("giftingMoneyEmailAddress")}
          validate={handleEmailAddressValidation("giftingMoneyEmailAddress")}
          validationError={errors.giftingMoneyEmailAddress ? errorMessages.giftingMoneyEmailAddress : null}
        />

        <TextQuestion
          question="What’s their relationship to you?"
          value={formState.giftingMoneyRelationship}
          valueEntered={handleInputChange("giftingMoneyRelationship")}
          validate={handleFieldValidation("giftingMoneyRelationship")}
          validationError={errors.giftingMoneyRelationship ? errorMessages.giftingMoneyRelationship : null}
        />

        <RadioQuestion
          question="Is this a gift from a joint account?"
          hint="Are there multiple people contributing to this gift?"
          options={YES_NO_OPTIONS}
          inline={true}
          name="giftingMoneyJointGift"
          selected={formState.giftingMoneyJointGift}
          optionSelected={handleInputChange("giftingMoneyJointGift")}
          validate={handleRadioValidation("giftingMoneyJointGift")}
          validationError={errors.giftingMoneyJointGift ? errorMessages.giftingMoneyJointGift : null}
        />

        {formState.giftingMoneyJointGift === true && (
          <RadioConditional>
            <TextQuestion
              question="Enter the joint account holder’s name"
              value={formState.giftingMoneyJointName}
              valueEntered={handleInputChange("giftingMoneyJointName")}
              validate={handleFieldValidation("giftingMoneyJointName")}
              validationError={errors.giftingMoneyJointName ? errorMessages.giftingMoneyJointName : null}
            />

            <TextQuestion
              question="What is the joint account holder’s email address?"
              hint="This must be the joint account holder’s email only — not the gifter’s above or anyone else involved in the case"
              value={formState.giftingMoneyJointEmailAddress}
              valueEntered={handleInputChange("giftingMoneyJointEmailAddress")}
              validate={handleEmailAddressValidation("giftingMoneyJointEmailAddress")}
              validationError={
                errors.giftingMoneyJointEmailAddress ? errorMessages.giftingMoneyJointEmailAddress : null
              }
            />

            <TextQuestion
              question="What’s the joint account holder’s relationship to you?"
              value={formState.giftingMoneyJointRelationship}
              valueEntered={handleInputChange("giftingMoneyJointRelationship")}
              validate={handleFieldValidation("giftingMoneyJointRelationship")}
              validationError={
                errors.giftingMoneyJointRelationship ? errorMessages.giftingMoneyJointRelationship : null
              }
            />
          </RadioConditional>
        )}

        <Action onClick={saveGiftingMoney}>Continue</Action>
      </Card>

      <Image src={imageCatAndHousePlant} />
    </React.Fragment>
  );
};

export default GiftingMoneyForm;
